'use client';

import { useTheme } from 'next-themes';
import type { ReactNode } from 'react';
import { useLayoutEffect } from 'react';
import { THEME_VARIABLES, useThemeOverrideStore } from '@/stores/theme-override';

interface ThemeOverrideProviderProps {
  children: ReactNode;
}

export function ThemeOverrideProvider({ children }: ThemeOverrideProviderProps): React.ReactNode {
  const { resolvedTheme } = useTheme();
  const lightOverrides = useThemeOverrideStore((s) => s.lightOverrides);
  const darkOverrides = useThemeOverrideStore((s) => s.darkOverrides);

  // useLayoutEffect ensures CSS var overrides are applied BEFORE child
  // useEffect callbacks run. Without this, chart components (which use useEffect)
  // read stale CSS vars from the previous theme during theme transitions.
  useLayoutEffect(() => {
    const el = document.documentElement;
    const mode = resolvedTheme === 'dark' ? 'dark' : 'light';
    const overrides = mode === 'dark' ? darkOverrides : lightOverrides;

    for (const tv of THEME_VARIABLES) {
      const value = overrides[tv.varName];
      if (value !== undefined) {
        el.style.setProperty(tv.varName, value);
      } else {
        el.style.removeProperty(tv.varName);
      }
    }
  }, [resolvedTheme, lightOverrides, darkOverrides]);

  return <>{children}</>;
}
